import { DateTime } from 'luxon';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';

export const CAMPAIGN_STATUS_FILTERS = [
  'all',
  'scheduled',
  'sent',
  'draft',
] as const;
export type CampaignStatusFilter = (typeof CAMPAIGN_STATUS_FILTERS)[number];

function isCampaignStatusFilter(
  value: string | null
): value is CampaignStatusFilter {
  return CAMPAIGN_STATUS_FILTERS.some((filter) => filter === value);
}

/** `?status=` válido vence; sem ele, `showScheduled=false` (URL antiga) vira 'sent'; resto 'all'. */
export function parseStatusFilter(
  statusParam: string | null,
  legacyShowScheduled: string | null
): CampaignStatusFilter {
  if (isCampaignStatusFilter(statusParam)) return statusParam;
  if (legacyShowScheduled === 'false') return 'sent';
  return 'all';
}

export function statusFilterToApi(
  filter: CampaignStatusFilter
): Array<CampaignDto['status']> | undefined {
  return filter === 'all' ? undefined : [filter];
}

export interface CampaignMonthGroup {
  key: string; // 'yyyy-MM'
  label: string;
  campaigns: CampaignDto[];
}

/** Agrupa na ordem recebida (API já vem desc); rótulo "novembro de 2026" no locale. */
export function groupCampaignsByMonth(
  campaigns: readonly CampaignDto[],
  locale: string
): CampaignMonthGroup[] {
  const groups: CampaignMonthGroup[] = [];
  for (const campaign of campaigns) {
    const launch = DateTime.fromISO(campaign.launchDateTime);
    const key = launch.toFormat('yyyy-MM');
    const last = groups.at(-1);
    if (last?.key === key) {
      last.campaigns.push(campaign);
      continue;
    }
    groups.push({
      key,
      label: launch
        .setLocale(locale)
        .toLocaleString({ month: 'long', year: 'numeric' }),
      campaigns: [campaign],
    });
  }
  return groups;
}

export type CampaignCardFacts =
  | {
      kind: 'results';
      paidOrderCount: number;
      revenue: number;
      ticket: number | null; // null se 0 pedidos -> "—"
    }
  | { kind: 'audience'; audienceName: string | null } // null -> "Todos os usuários"
  | { kind: 'none' }; // enviada sem analytics

export function getCampaignCardFacts(
  c: CampaignDto,
  analyticsEnabled: boolean
): CampaignCardFacts {
  if (c.status !== 'sent') {
    return { kind: 'audience', audienceName: c.audience?.name ?? null };
  }
  if (!analyticsEnabled) return { kind: 'none' };
  const { paidOrderCount, revenue } = c.analytics;
  return {
    kind: 'results',
    paidOrderCount,
    revenue,
    ticket: paidOrderCount === 0 ? null : revenue / paidOrderCount,
  };
}

/** Mesma regra da lista (`canDeleteCampaigns`) e do backend: só o que não foi enviado. */
export function canCancelCampaign(c: Pick<CampaignDto, 'status'>): boolean {
  return c.status !== 'sent';
}
